import React, { useEffect, useState, useRef } from "react";
import { ShoppingBag, Eye, TrendingUp, CalendarDays } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import http from "@/api";
import { useApp } from "@/context";

/**
 * LiveActivityTicker — narrow social-proof strip rendered above the page hero
 * on customer routes only. Cycles through anonymized recent orders + aggregate
 * stats every ~5 seconds with slide animation.
 *
 * Backend: GET /api/activity-ticker (cached 30s, public).
 * Hides if no events (defensive). Skips on /admin /staff /print /track etc.
 */
const ROTATE_MS = 5000;
const REFRESH_MS = 60_000;

const ICONS = {
  shopping: ShoppingBag,
  eye: Eye,
  trending: TrendingUp,
  calendar: CalendarDays,
};

export default function LiveActivityTicker() {
  const { lang } = useApp();
  const ar = lang === "ar";
  const [events, setEvents] = useState([]);
  const [idx, setIdx] = useState(0);
  // Persist dismissal across page reloads — re-show after 24h to keep social
  // proof eventually visible again for returning visitors.
  const [closed, setClosed] = useState(() => {
    try {
      const dismissedAt = parseInt(localStorage.getItem("rg_ticker_dismissed_at") || "0", 10);
      return dismissedAt && (Date.now() - dismissedAt) < 24 * 60 * 60 * 1000;
    } catch { return false; }
  });
  const refreshTimer = useRef(null);

  const dismiss = () => {
    setClosed(true);
    try { localStorage.setItem("rg_ticker_dismissed_at", String(Date.now())); } catch { /**/ }
  };

  const fetchTicker = async () => {
    try {
      const { data } = await http.get("/activity-ticker");
      if (data?.events?.length) setEvents(data.events);
    } catch { /* silent */ }
  };

  useEffect(() => {
    if (closed) return;
    fetchTicker();
    refreshTimer.current = setInterval(fetchTicker, REFRESH_MS);
    return () => clearInterval(refreshTimer.current);
  }, [closed]);

  useEffect(() => {
    if (!events.length) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % events.length), ROTATE_MS);
    return () => clearInterval(t);
  }, [events.length]);

  if (closed || !events.length) return null;
  const ev = events[idx % events.length];
  if (!ev) return null;

  return (
    <div className="w-full overflow-hidden border-b"
          style={{ background: "linear-gradient(90deg, rgba(0,0,0,0.05), rgba(0,0,0,0.10), rgba(0,0,0,0.05))",
                   borderColor: "rgba(0,0,0,0.25)" }}
          data-testid="live-activity-ticker">
      <div className="max-w-6xl mx-auto px-3 sm:px-5 py-1.5 flex items-center gap-3">
        <div className="flex items-center gap-1.5 shrink-0">
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-70"
                   style={{ background: "#10b981" }}></span>
            <span className="relative inline-flex rounded-full h-2 w-2"
                   style={{ background: "#10b981" }}></span>
          </span>
          <span className="text-[10px] uppercase tracking-widest font-semibold opacity-60 hidden sm:inline">
            LIVE
          </span>
        </div>

        <div className="flex-1 min-w-0 relative h-5">
          <AnimatePresence mode="wait">
            <motion.div
              key={`${idx}-${ev.type}`}
              initial={{ y: 14, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: -14, opacity: 0 }}
              transition={{ duration: 0.35, ease: "easeOut" }}
              className="absolute inset-0 flex items-center gap-2 text-xs sm:text-sm whitespace-nowrap overflow-hidden"
              data-testid={`ticker-event-${idx}`}>
              <EventBody ar={ar} ev={ev} />
            </motion.div>
          </AnimatePresence>
        </div>

        <button onClick={dismiss}
                 data-testid="ticker-close"
                 aria-label="Dismiss"
                 className="text-xs opacity-40 hover:opacity-80 transition shrink-0 px-1">
          ✕
        </button>
      </div>
    </div>
  );
}

function EventBody({ ar, ev }) {
  const Icon = ICONS[ev.icon] || ShoppingBag;
  const minutesText = (n) =>
    !n ? "" : ar
      ? n < 1 ? "قبل لحظات" : `قبل ${n} دقيقة`
      : n < 1 ? "moments ago" : `${n} min ago`;

  if (ev.type === "order") {
    const ago = ev.minutes_ago ?? minutesAgoFromIso(ev.at);
    return (
      <span className="flex items-center gap-1.5 truncate">
        <Icon size={13} className="shrink-0" style={{ color: "#000000" }} />
        <span className="font-semibold truncate" style={{ color: "#000000" }}>{ev.name_short}</span>
        {ev.city && <span className="opacity-70">· {ev.city}</span>}
        <span className="opacity-80 truncate">
          {ar ? "طلب" : "ordered"} <span className="font-medium">{ev.item}</span>
        </span>
        <span className="text-[10px] opacity-50 shrink-0 hidden sm:inline">{minutesText(ago)}</span>
      </span>
    );
  }
  if (ev.type === "viewers") {
    return (
      <span className="flex items-center gap-1.5">
        <Icon size={13} style={{ color: "#0ea5e9" }} />
        <span className="font-semibold">{ev.count}</span>
        <span className="opacity-80">{ar ? "شخص يتصفّح الموقع الآن 👀" : "people browsing right now 👀"}</span>
      </span>
    );
  }
  if (ev.type === "stat") {
    const window = ev.window === "hour" ? (ar ? "في آخر ساعة" : "in the last hour")
                                         : (ar ? "اليوم" : "today");
    return (
      <span className="flex items-center gap-1.5">
        <Icon size={13} style={{ color: "#000000" }} />
        <span className="font-semibold" style={{ color: "#000000" }}>{ev.count}</span>
        <span className="opacity-80">{ar ? `طلب نُفّذ ${window} 🌹` : `orders fulfilled ${window} 🌹`}</span>
      </span>
    );
  }
  return null;
}

function minutesAgoFromIso(iso) {
  if (!iso) return null;
  try {
    const t = new Date(iso).getTime();
    return Math.max(0, Math.round((Date.now() - t) / 60000));
  } catch { return null; }
}
